import React, { useCallback, useState, useRef } from 'react'
import {
  View,
  ScrollView,
  Modal,
  TouchableWithoutFeedback,
  Dimensions,
} from 'react-native'
import { Icon, Text, TouchableRipple } from 'react-native-paper'
import { createStyle } from '../../contexts/global-styles.provider'
import { MelddSearchBar } from './meldd-search.input'

export interface AutocompleteItem {
  id: string | number
  label: string
  disabled?: boolean
  icon?: string
  renderItem?: (item: AutocompleteItem) => React.ReactNode
}

interface MelddAutocompleteInputProps {
  placeholder?: string
  value: string
  onChangeText: (text: string) => void
  items: AutocompleteItem[]
  onSelect: (item: AutocompleteItem) => void
  loading?: boolean
  minSearchLength?: number
  maxHeight?: number
  emptyMessage?: string
}

export const MelddAutocompleteInput: React.FC<MelddAutocompleteInputProps> = ({
  placeholder,
  value,
  onChangeText,
  items,
  onSelect,
  loading = false,
  minSearchLength = 2,
  maxHeight = 200,
  emptyMessage,
}) => {
  const styles = useStyle()
  const [isFocused, setIsFocused] = useState(false)
  const [inputLayout, setInputLayout] = useState<{
    x: number
    y: number
    width: number
    height: number
  } | null>(null)
  const inputRef = useRef<View>(null)
  const windowHeight = Dimensions.get('window').height

  const showSuggestions =
    value.length >= minSearchLength && items.length > 0 && isFocused

  const handleSearchChange = useCallback(
    (text: string) => {
      onChangeText(text)
    },
    [onChangeText]
  )

  const handleSelect = useCallback(
    (item: AutocompleteItem) => {
      if (!item.disabled) {
        onSelect(item)
        onChangeText('')
        setIsFocused(false)
      }
    },
    [onSelect, onChangeText]
  )

  const handleInputLayout = useCallback(() => {
    inputRef.current?.measure((x, y, width, height, pageX, pageY) => {
      setInputLayout({ x: pageX, y: pageY, width, height })
    })
  }, [])

  const handleFocus = useCallback(() => {
    setIsFocused(true)
    // Measure immediately and again after a short delay to ensure accurate positioning
    handleInputLayout()
    setTimeout(handleInputLayout, 100)
  }, [handleInputLayout])

  const handleBlur = useCallback(() => {
    setTimeout(() => setIsFocused(false), 200)
  }, [])

  const handleDismiss = useCallback(() => {
    setIsFocused(false)
  }, [])

  const getDropdownPosition = useCallback(() => {
    if (!inputLayout) return { top: 0, left: 0, width: 0 }

    const windowWidth = Dimensions.get('window').width
    const top = inputLayout.y + inputLayout.height + 4
    const left = inputLayout.x
    const width = inputLayout.width

    // Adjust if dropdown would go off screen
    const adjustedLeft = Math.max(8, Math.min(left, windowWidth - width - 8))
    const maxDropdownHeight = maxHeight || 200
    const adjustedTop =
      top + maxDropdownHeight > windowHeight
        ? Math.max(8, inputLayout.y - maxDropdownHeight - 4)
        : top

    return {
      top: adjustedTop,
      left: adjustedLeft,
      width: Math.min(width, windowWidth - 16),
    }
  }, [inputLayout, maxHeight, windowHeight])

  const dropdownPosition = getDropdownPosition()

  return (
    <View style={styles.container} ref={inputRef} onLayout={handleInputLayout}>
      <MelddSearchBar
        placeholder={placeholder}
        value={value}
        onChangeText={handleSearchChange}
        collapsed={false}
        {...({
          onFocus: handleFocus,
          onBlur: handleBlur,
        } as any)}
      />
      <Modal
        visible={
          showSuggestions ||
          (value.length >= minSearchLength &&
            items.length === 0 &&
            !loading &&
            !!emptyMessage)
        }
        transparent
        animationType="none"
        onRequestClose={handleDismiss}
      >
        <TouchableWithoutFeedback onPress={handleDismiss}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View
                style={[
                  styles.suggestionsContainer,
                  {
                    maxHeight,
                    top: dropdownPosition.top,
                    left: dropdownPosition.left,
                    width: dropdownPosition.width,
                  },
                ]}
              >
                {items.length > 0 ? (
                  <ScrollView
                    style={styles.suggestionsList}
                    keyboardShouldPersistTaps="handled"
                    nestedScrollEnabled={true}
                  >
                    {items.map((item) => (
                      <TouchableRipple
                        key={item.id}
                        onPress={() => handleSelect(item)}
                        disabled={item.disabled}
                        style={styles.suggestionItem}
                      >
                        <View style={styles.suggestionContent}>
                          {item.renderItem ? (
                            item.renderItem(item)
                          ) : (
                            <>
                              <Text>{item.label}</Text>
                              {item.icon && (
                                <Icon source={item.icon} size={20} />
                              )}
                            </>
                          )}
                        </View>
                      </TouchableRipple>
                    ))}
                  </ScrollView>
                ) : (
                  value.length >= minSearchLength &&
                  !loading &&
                  emptyMessage && (
                    <View style={styles.emptyContainer}>
                      <Text style={styles.emptyText}>{emptyMessage}</Text>
                    </View>
                  )
                )}
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </View>
  )
}

const useStyle = createStyle(({ theme }) => ({
  container: {
    position: 'relative',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  suggestionsContainer: {
    position: 'absolute',
    backgroundColor: theme.colors.surface,
    borderRadius: 8,
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  suggestionsList: {
    maxHeight: 200,
  },
  suggestionItem: {
    paddingHorizontal: theme.spacing(2),
    paddingVertical: theme.spacing(1.5),
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.outlineVariant,
  },
  suggestionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  emptyContainer: {
    padding: theme.spacing(2),
  },
  emptyText: {
    color: theme.colors.onSurfaceVariant,
    textAlign: 'center',
  },
}))
